import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { MapTrack } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    InputDateTime,
    DateComp,
    InputColor,
    DisplayColor,
    InputNullableFloat,
    DisplayDistance,
    InputSelect,
    DataSourceLookup,
} from '@/components';
import { type DateType } from '@/utilities/dateutils';
import { useCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class MapTrackGuiDef extends GuiDef<MapTrack> {
    constructor() {
        super('MapTrack');

        const Common = storeToRefs(useCommonStore());
        const OrganizersSource = computed(() => {
            return Common.OrganizersAsSelectLine.value;
        });

        const Name = new FieldDef<MapTrack>();
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'Both';
        Name.TranslationKey = 'maptrack.name';
        Name.Editable = false;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (maptrack) => maptrack.Name;
        Name.setValue = () => {};
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = {};
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = null;
        Name.SortDirection = undefined;
        Name.cellClassRules = undefined;
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const Created = new FieldDef<MapTrack>();
        Created.Name = 'Created';
        Created.FieldName = 'Created';
        Created.DefinedFor = 'List';
        Created.TranslationKey = 'maptrack.created';
        Created.Editable = false;
        Created.Filterable = true;
        Created.Sortable = true;
        Created.CssClasses = '';
        Created.value = (maptrack) => maptrack.Created;
        Created.setValue = () => {};
        Created.DetailEditor = markRaw(InputDateTime);
        Created.DetailEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        Created.CellEditor = markRaw(InputDateTime);
        Created.CellEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        Created.CellRenderer = markRaw(DateComp);
        Created.CellRendererProps = { format: 'DATETIME' as DateType, readonly: true };
        Created.Validator = null;
        Created.SortDirection = undefined;
        Created.cellClassRules = undefined;
        Created.MinWidth = 180;
        Created.MaxWidth = undefined;
        Created.Tooltip = null;
        this.Fields.push(Created);

        const LineColor = new FieldDef<MapTrack>();
        LineColor.Name = 'LineColor';
        LineColor.FieldName = 'LineColor';
        LineColor.DefinedFor = 'Both';
        LineColor.TranslationKey = 'maptrack.linecolor';
        LineColor.Editable = false;
        LineColor.Filterable = true;
        LineColor.Sortable = true;
        LineColor.CssClasses = '';
        LineColor.value = (maptrack) => maptrack.LineColor;
        LineColor.setValue = () => {};
        LineColor.DetailEditor = markRaw(InputColor);
        LineColor.DetailEditorProps = {};
        LineColor.CellEditor = markRaw(InputColor);
        LineColor.CellEditorProps = {};
        LineColor.CellRenderer = markRaw(DisplayColor);
        LineColor.CellRendererProps = {};
        LineColor.Validator = null;
        LineColor.SortDirection = undefined;
        LineColor.cellClassRules = undefined;
        LineColor.MinWidth = undefined;
        LineColor.MaxWidth = undefined;
        LineColor.Tooltip = null;
        this.Fields.push(LineColor);

        const TrackLength = new FieldDef<MapTrack>();
        TrackLength.Name = 'TrackLength';
        TrackLength.FieldName = 'TrackLength';
        TrackLength.DefinedFor = 'List';
        TrackLength.TranslationKey = 'maptrack.tracklength';
        TrackLength.Editable = false;
        TrackLength.Filterable = true;
        TrackLength.Sortable = true;
        TrackLength.CssClasses = '';
        TrackLength.value = (maptrack) => maptrack.TrackLength;
        TrackLength.setValue = () => {};
        TrackLength.DetailEditor = markRaw(InputNullableFloat);
        TrackLength.DetailEditorProps = {};
        TrackLength.CellEditor = markRaw(InputNullableFloat);
        TrackLength.CellEditorProps = {};
        TrackLength.CellRenderer = markRaw(DisplayDistance);
        TrackLength.CellRendererProps = {};
        TrackLength.Validator = null;
        TrackLength.SortDirection = undefined;
        TrackLength.cellClassRules = undefined;
        TrackLength.MinWidth = undefined;
        TrackLength.MaxWidth = undefined;
        TrackLength.Tooltip = null;
        this.Fields.push(TrackLength);

        const Description = new FieldDef<MapTrack>();
        Description.Name = 'Description';
        Description.FieldName = 'Description';
        Description.DefinedFor = 'Both';
        Description.TranslationKey = 'maptrack.description';
        Description.Editable = false;
        Description.Filterable = true;
        Description.Sortable = true;
        Description.CssClasses = '';
        Description.value = (maptrack) => maptrack.Description;
        Description.setValue = () => {};
        Description.DetailEditor = markRaw(InputText);
        Description.DetailEditorProps = {};
        Description.CellEditor = markRaw(InputText);
        Description.CellEditorProps = {};
        Description.CellRenderer = markRaw(ValidatedText);
        Description.CellRendererProps = {};
        Description.Validator = null;
        Description.SortDirection = undefined;
        Description.cellClassRules = undefined;
        Description.MinWidth = undefined;
        Description.MaxWidth = undefined;
        Description.Tooltip = null;
        this.Fields.push(Description);

        const OrganizerID = new FieldDef<MapTrack>();
        OrganizerID.Name = 'OrganizerID';
        OrganizerID.FieldName = 'OrganizerID';
        OrganizerID.DefinedFor = 'Both';
        OrganizerID.TranslationKey = 'maptrack.organizerid';
        OrganizerID.Editable = false;
        OrganizerID.Filterable = false;
        OrganizerID.Sortable = true;
        OrganizerID.CssClasses = '';
        OrganizerID.value = (maptrack) => maptrack.OrganizerID;
        OrganizerID.setValue = () => {};
        OrganizerID.DetailEditor = markRaw(InputSelect);
        OrganizerID.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: OrganizersSource }, readonly: true };
        OrganizerID.CellEditor = markRaw(InputSelect);
        OrganizerID.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: OrganizersSource },
            readonly: true,
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        OrganizerID.CellRenderer = markRaw(DataSourceLookup);
        OrganizerID.CellRendererProps = { datasource: { mapsource: OrganizersSource }, readonly: true, hideCaret: true };
        OrganizerID.ExportValueFormatter = (obj: MapTrack) => {
            return OrganizersSource.value.get(obj.OrganizerID)?.text;
        };
        OrganizerID.Validator = null;
        OrganizerID.SortDirection = undefined;
        OrganizerID.cellClassRules = undefined;
        OrganizerID.MinWidth = undefined;
        OrganizerID.MaxWidth = undefined;
        OrganizerID.Tooltip = null;
        this.Fields.push(OrganizerID);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['Organizers']);
    }
}
